import { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface StripeCardFormProps {
  clientSecret: string;
  setupIntentId: string;
  stripeMode: "test" | "live";
  onSuccess: (result: { card_brand: string; card_last4: string; card_exp_month?: number; card_exp_year?: number }) => void;
  onError: (message: string) => void;
  savePaymentMethod: (args: { payment_method_id: string; stripe_mode: "test" | "live" }) => Promise<{ card_brand?: string; card_last4?: string; card_exp_month?: number; card_exp_year?: number }>;
}

export const StripeCardForm = ({ 
  clientSecret, 
  setupIntentId, 
  stripeMode,
  onSuccess, 
  onError,
  savePaymentMethod 
}: StripeCardFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        onError("Card element not found");
        return;
      }

      const { error, setupIntent: confirmedIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        onError(error.message || "Failed to setup card");
        return;
      }

      const rawPm = confirmedIntent?.payment_method;
      const paymentMethodId =
        typeof rawPm === "string"
          ? rawPm
          : (rawPm && typeof rawPm === "object" && "id" in rawPm && typeof (rawPm as { id: string }).id === "string")
            ? (rawPm as { id: string }).id
            : null;
      if (!paymentMethodId || !paymentMethodId.startsWith("pm_")) {
        onError("Could not get payment method from Stripe. Please try again.");
        return;
      }

      const result = await savePaymentMethod({
        payment_method_id: paymentMethodId,
        stripe_mode: stripeMode,
      });
      
      if (result.card_brand && result.card_last4) {
        onSuccess({
          card_brand: result.card_brand,
          card_last4: result.card_last4,
          ...(result.card_exp_month != null && { card_exp_month: result.card_exp_month }),
          ...(result.card_exp_year != null && { card_exp_year: result.card_exp_year }),
        });
      } else {
        onSuccess({ card_brand: "card", card_last4: "****" });
      }
    } catch (error: any) {
      onError(error.message || "Failed to setup payment method");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border rounded-lg bg-background">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": {
                  color: "#aab7c4",
                },
              },
              invalid: {
                color: "#9e2146",
              },
            },
          }}
        />
      </div>
      <Button 
        type="submit" 
        className="w-full" 
        disabled={!stripe || isProcessing}
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          "Save Card"
        )}
      </Button>
    </form>
  );
};
